import { Injectable } from '@angular/core';
import {
  FilterGroup,
  FilterGroupFiltersInner,
  SearchFilter,
  SearchReportRequest,
} from 'ldt-people-api';
import { Log } from '../logger.service';
import { getPeopleColumnByName } from './people-columns';
import { ColumnType } from '../shared/dw-column';

export interface QuickBuild {
  id: string;
  name: string;
  rootFilterGroup: UIFilterGroup;
  description?: string;
  reports?: any;
}

export interface SubFiltersFilter {
  field: string; // Only job_role supported for now
  name?: string;
  id?: string;
  filters?: UIFilterGroup;
}
export interface UIFilter {
  filter: SearchFilter | SubFiltersFilter;
}

// HACK. Not sure about this, but it lets us cast when we know what kind of filter we have
export interface UIFilterWithSearchFilter extends UIFilter {
  filter: SearchFilter;
}
export interface UIFilterWithSubFiltersFilter extends UIFilter {
  filter: SubFiltersFilter;
}

export interface UIReportRequest {
  name: string;
  params: any;
}

// params object for a report request
interface ReportParams {
  field?: string;
  group_by?: string[];
  [key: string]: any;
}

// The UI's representation of a filter group, including some UI-specific properties
export interface UIFilterGroup {
  operator: 'and' | 'or';
  filters: Array<UIFilter | UIFilterGroup>;
  isJobsGroup?: boolean;
  jobsGroupType?: 'any' | 'active' | 'ended';
  fieldsToChart?: FieldsToChart[];
  id?: string;
  name?: string;
  positionStatus?: PositionStatus;
  reports?: any;
}

// Where supported (root filter group and job groups), holds the user-selected fields to chart and the chart type
export interface FieldsToChart {
  columnName: string;
  chartType: ChartType;
}

export enum ChartType {
  Bar = 'bar',
  Line = 'line',
  Pie = 'pie',
}

export enum PositionStatus {
  All = 'all',
  First = 'first',
  Last = 'last',
  Promotion = 'promotion',
}

// A simple class to generate unique IDs for each filter group for this session
export class UniqueIdGenerator {
  public static generate(): string {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8);
    return `${timestamp}-${randomStr}`;
  }
}

@Injectable({
  providedIn: 'root',
})
export class QuickBuildService {
  // TODO (some day):
  // Improve serialization to add compression
  static serializeQuickBuild(qb: QuickBuild): string {
    const searchParams = {
      id: qb.id,
      name: qb.name,
      rootFilterGroup: qb.rootFilterGroup,
      quickBuild: true,
    };

    return JSON.stringify(searchParams);
  }

  // TODO (some day):
  // Improve deserialization to add schema validation
  static deserializeQuickBuild(serializedString: string): QuickBuild {
    let parsedParams = JSON.parse(serializedString);

    const qb: QuickBuild = {
      id: parsedParams.id || 0,
      name: parsedParams.name || '',
      rootFilterGroup: parsedParams.rootFilterGroup,
    };

    return qb;
  }

  // Collect all the report IDs in the provided QB. Can be used by components to
  // gather the reports for display using the viz gactory
  static getAllReportIDsInQuickbuildGrouped(quickBuild: QuickBuild): { [key: string]: string[] } {
    const reports: { [key: string]: string[] } = {};
    const searchFilterGroups = (group: UIFilterGroup): void => {
      if (group.reports) {
        group.reports.forEach((r: any) => {
          if (!reports[group.name || group.id || '']) {
            reports[group.name || group.id || ''] = [];
          }
          reports[group.name || group.id || ''].push(r.id);
        });
      }
      group.filters.forEach((filter) => {
        if (QuickBuildService.isUIFilterGroup(filter)) {
          searchFilterGroups(filter as UIFilterGroup);
        }
      });
    };
    searchFilterGroups(quickBuild.rootFilterGroup);
    return reports;
  }

  // Given a quick build and a report ID, return the report and the ID of the filter group
  // that contains it.
  static getReportById(
    quickBuild: QuickBuild,
    reportId: string
  ): [UIReportRequest, string] | undefined {
    // First check root level reports
    const rootReport = quickBuild.rootFilterGroup.reports?.find((r: any) => r.id === reportId);
    if (rootReport) {
      return [rootReport, quickBuild.rootFilterGroup.id || ''];
    }

    // Helper function to recursively search through filter groups
    const searchFilterGroups = (group: UIFilterGroup): [UIReportRequest, string] | undefined => {
      Log.d('quick-build.service getReportById searchFilterGroups', group, reportId);

      // Check reports in current group
      const report = group.reports?.find((r: any) => r.id === reportId);
      if (report) {
        return [report, group.id || ''];
      }

      // Search through nested filter groups
      for (const filter of group.filters) {
        if (QuickBuildService.isUIFilterGroup(filter)) {
          // This is a FilterGroup
          const nestedReport = searchFilterGroups(filter as UIFilterGroup);
          if (nestedReport) {
            return nestedReport;
          }
        }
      }
      return undefined;
    };

    return searchFilterGroups(quickBuild.rootFilterGroup);
  }

  static getFiltersFromQuickBuild(quickBuild: QuickBuild, fieldName: string): UIFilter[] {
    const filters: UIFilter[] = [];
    const searchFilterGroups = (group: UIFilterGroup): void => {
      group.filters.forEach((filter) => {
        if (QuickBuildService.isUIFilterGroup(filter)) {
          searchFilterGroups(filter as UIFilterGroup);
        } else if (filter.filter.field === fieldName) {
          filters.push(filter as UIFilter);
        }
      });
    };
    searchFilterGroups(quickBuild.rootFilterGroup);
    return filters;
  }

  // Given a group ID, find the corresponding filter group
  static findFilterGroupById(id: string, group: UIFilterGroup): UIFilterGroup | undefined {
    if (!group) {
      return undefined;
    }

    if (group.id === id) {
      return group;
    }

    for (const filter of group.filters) {
      if (QuickBuildService.isUIFilterGroup(filter)) {
        const found = QuickBuildService.findFilterGroupById(id, filter as UIFilterGroup);
        if (found) {
          return found;
        }
      }
    }

    return undefined;
  }

  public static addFiltersToFilterGroup(
    quickBuild: QuickBuild,
    filters: (UIFilter | UIFilterGroup)[],
    groupId: string
  ): void {
    if (groupId === '__root__') {
      quickBuild.rootFilterGroup.filters.push(...filters);
      return;
    }

    const group = QuickBuildService.findFilterGroupById(groupId, quickBuild.rootFilterGroup);
    if (group) {
      group.filters.push(...filters);
    }
  }

  static isUIFilter(item: UIFilter | UIFilterGroup): item is UIFilter {
    return !('operator' in item);
  }

  static isUIFilterGroup(item: UIFilter | UIFilterGroup): item is UIFilterGroup {
    return 'operator' in item;
  }

  static isSubFiltersFilter(item: SearchFilter | SubFiltersFilter): item is SubFiltersFilter {
    const fieldName = item.field;
    if (!fieldName) return false;

    try {
      const col = getPeopleColumnByName(fieldName);
      return col.type === ColumnType.subFilters;
    } catch (error) {
      Log.s('QuickBuildService isSubFiltersFilter: field not found', 'error', {
        fieldName: fieldName,
      });
      return false;
    }
  }

  // Create People API search filters from a root filter group and return the filters and aggs
  static rootFilterGroupToAPIFilters(
    rootFilterGroup: UIFilterGroup,
    reportIdToInclude: string | null = null
  ): [FilterGroup, null] {
    Log.d('rootFilterGroupToAPIFilters', rootFilterGroup, reportIdToInclude);
    const [filters, rootAggs] = QuickBuildService.filterGroupToAPIFilters(
      rootFilterGroup,
      reportIdToInclude
    );

    const topFilter: FilterGroup = {
      filters: filters,
      report: reportIdToInclude
        ? (() => {
            const report = rootFilterGroup.reports?.find((r: any) => r.id === reportIdToInclude);
            if (report) {
              return this.uiReportToAPIReport(report);
            }
            return undefined;
          })()
        : undefined,
    };
    Log.d('topFilter', topFilter);

    // If the top-level UI operator is an or, we need to wrap it since the top-level
    // filters only support and
    if (rootFilterGroup.operator === FilterGroup.OperatorEnum.Or) {
      topFilter.filters = [
        {
          operator: 'or',
          filters: filters,
        },
      ];
    }

    return [topFilter, null];
  }

  public static copyQuickBuild(qb: QuickBuild): QuickBuild {
    return JSON.parse(JSON.stringify(qb));
  }

  public static copyQuickBuildWithoutReports(qb: QuickBuild): QuickBuild {
    const copy = this.copyQuickBuild(qb);
    // recursively delete reports from all filter groups
    const deleteReports = (group: UIFilterGroup): void => {
      delete group.reports;
      group.filters.forEach((filter) => {
        if (QuickBuildService.isUIFilterGroup(filter)) {
          deleteReports(filter as UIFilterGroup);
        }
      });
    };
    deleteReports(copy.rootFilterGroup);
    return copy;
  }

  // Converts a UI report request to an API report request
  private static uiReportToAPIReport(
    uiReport: UIReportRequest, // the UI report request to convert
    changeJobsToPosition: boolean = false // transform 'jobs' fields to 'position' fields
  ): SearchReportRequest | undefined {
    if (!uiReport) {
      return undefined;
    }

    try {
      let params: ReportParams = JSON.parse(JSON.stringify(uiReport.params));
      if (changeJobsToPosition) {
        params.field = params.field?.replace(/^jobs/, 'position');
        if (params.group_by) {
          params.group_by = params.group_by.map((g: string) => g.replace(/^jobs/, 'position'));
        }
      }

      // returns the API report request (or undefined if the input is invalid)
      return {
        name: uiReport.name as SearchReportRequest.NameEnum,
        params: params,
      };
    } catch (e) {
      Log.e('Error converting UI report to API report', e);
      return undefined;
    }
  }

  private static filterGroupToAPIFilters(
    group: UIFilterGroup,
    reportIdToInclude: string | number | null = null
  ): [FilterGroupFiltersInner[], null] {
    const filters: FilterGroupFiltersInner[] = [];

    if (group.filters) {
      group.filters.forEach((filter, idx) => {
        if (QuickBuildService.isUIFilterGroup(filter)) {
          // This is a FilterGroup
          filter = filter as UIFilterGroup;

          // If this is a jobs group, we handle all the filters without recursion so we can
          // modify them in special ways
          if (filter.isJobsGroup) {
            const groupFilters: FilterGroupFiltersInner[] = [];
            const jobsGroupType = filter.jobsGroupType || 'any';

            // If they only want ended jobs, add a filter for that
            if (jobsGroupType === 'ended') {
              groupFilters.push({
                field: 'jobs.ended_at',
                type: SearchFilter.TypeEnum.Must,
                match_type: SearchFilter.MatchTypeEnum.Exists,
              });
            } else if (jobsGroupType === 'active') {
              // Create the UIFilter and use our helper to add the actual API filters
              const jobActiveAtFilter: UIFilter = {
                filter: {
                  field: 'job_active_at',
                  match_type: SearchFilter.MatchTypeEnum.Fuzzy,
                  type: SearchFilter.TypeEnum.Must,
                  date_from: 'now/M',
                },
              };
              const jobActiveAtFilters = this.generateJobActiveAtAPIFilters(jobActiveAtFilter);
              if (jobActiveAtFilters) {
                groupFilters.push(...jobActiveAtFilters);
              }
            }

            // Set filters for position status
            switch (filter.positionStatus) {
              case PositionStatus.First:
                groupFilters.push({
                  field: 'jobs.is_first_at_company',
                  type: SearchFilter.TypeEnum.Must,
                  match_type: SearchFilter.MatchTypeEnum.Exact,
                  boolean_value: true,
                });
                break;
              case PositionStatus.Last:
                groupFilters.push({
                  field: 'jobs.is_last_at_company',
                  type: SearchFilter.TypeEnum.Must,
                  match_type: SearchFilter.MatchTypeEnum.Exact,
                  boolean_value: true,
                });
                break;
              case PositionStatus.Promotion:
                groupFilters.push({
                  field: 'jobs.is_first_at_company',
                  type: SearchFilter.TypeEnum.Must,
                  match_type: SearchFilter.MatchTypeEnum.Exact,
                  boolean_value: false,
                });
                break;
            }

            filter.filters.forEach((f) => {
              // All filters in job groups are UI filters. Get a copy for modification...
              const apiFilter = JSON.parse(JSON.stringify(f)) as UIFilter;

              if (this.isSubFiltersFilter(apiFilter.filter)) {
                if (!apiFilter.filter.filters) {
                  return;
                }

                const subFilters = this.filterGroupToAPIFilters(apiFilter.filter.filters);
                if (subFilters[0]) {
                  groupFilters.push(...subFilters[0]);
                }
                return;
              }

              if (!this.hasValues(apiFilter.filter)) {
                return;
              }

              if (apiFilter.filter.field?.endsWith('_search')) {
                const searchFilters = this.generateCompanySearchAPIFilters(apiFilter);
                if (searchFilters) {
                  groupFilters.push(searchFilters);
                }
              } else if (apiFilter.filter.field === 'job_active_at') {
                const jobActiveAtFilters = this.generateJobActiveAtAPIFilters(apiFilter);
                if (jobActiveAtFilters) {
                  groupFilters.push(...jobActiveAtFilters);
                }
              } else {
                groupFilters.push(apiFilter.filter);
              }
            });

            filters.push({
              operator: 'and',
              filters: groupFilters,
              report: reportIdToInclude
                ? QuickBuildService.uiReportToAPIReport(
                    filter.reports?.find((r: any) => r.id === reportIdToInclude),
                    false
                  )
                : undefined,
            });
          } else {
            const [innerFilters, rootAggs] = this.filterGroupToAPIFilters(
              filter,
              reportIdToInclude
            );

            filters.push({
              operator: filter.operator,
              filters: innerFilters,
              report: reportIdToInclude
                ? QuickBuildService.uiReportToAPIReport(
                    filter.reports?.find((r: any) => r.id === reportIdToInclude),
                    false
                  )
                : undefined,
            });
          }
        } else {
          // This is a UIFilter
          filter = filter as UIFilter;

          if (this.isSubFiltersFilter(filter.filter)) {
            if (!filter.filter.filters) {
              return;
            }

            const subFilters = this.filterGroupToAPIFilters(filter.filter.filters);
            if (subFilters[0]) {
              filters.push(...subFilters[0]);
            }
            return;
          }

          if (!this.hasValues(filter.filter)) {
            return;
          }

          // Change _search filters to search for company ID
          if (filter.filter.field?.endsWith('_search')) {
            const searchFilters = this.generateCompanySearchAPIFilters(filter);
            if (searchFilters) {
              filters.push(searchFilters);
            }
          } else if (filter.filter.field === 'job_active_at') {
            const jobActiveAtFilters = this.generateJobActiveAtAPIFilters(filter);
            if (jobActiveAtFilters) {
              filters.push(...jobActiveAtFilters);
            }
          } else {
            filters.push(filter.filter);
          }
        }
      });
    }
    return [filters, null];
  }

  // Given a UIFilter that represents a company search, generates the nested filters to find
  // matching companies and company groups
  private static generateCompanySearchAPIFilters(filter: UIFilter): FilterGroupFiltersInner | null {
    const searchFilter = filter.filter as SearchFilter;
    // If this is a company search field, we need to create the API filters for the companies and groups
    if (!searchFilter.string_values) return null;

    const groupValues = searchFilter.string_values.filter((v) => v.endsWith('-group')) || [];

    // We add a filter the company ID for all provided string values. This ensures we capture the actual
    // parents
    const filtersForCompany: FilterGroupFiltersInner[] = [];
    if (searchFilter.string_values.length > 0) {
      filtersForCompany.push({
        field: searchFilter.field!.replace('_search', '.company.id'),
        type: searchFilter.type,
        match_type: searchFilter.match_type,
        string_values: searchFilter.string_values.map((v) => v.replace('-group', '')),
      });
    }

    // For any grouped companies, we add another filter for the group ID
    if (groupValues.length > 0) {
      filtersForCompany.push({
        field: searchFilter.field!.replace('_search', '.company.group_id'),
        type: searchFilter.type,
        match_type: searchFilter.match_type,
        string_values: groupValues.map((v) => v.replace('-group', '')),
      });
    }

    // Match on either the provided company, or the parent companies if any were provided
    return {
      operator: searchFilter.type === SearchFilter.TypeEnum.Must ? 'or' : 'and',
      filters: filtersForCompany,
    };
  }

  // Generates filters that match on jobs that were active at the given date range
  // If only date_from is provided, it will match on jobs that were active at that date
  // If date_to is also provided, it will match on jobs that were active at any point in that range
  private static generateJobActiveAtAPIFilters(
    filter: UIFilter
  ): FilterGroupFiltersInner | null | any {
    const searchFilter = filter.filter as SearchFilter;
    if (!searchFilter.date_from) return null;

    // If no date_to is provided, use the date_from as the date_to
    const date_to = searchFilter.date_to || searchFilter.date_from;

    const activeStartDateFilter: FilterGroupFiltersInner = {
      field: 'jobs.started_at',
      match_type: SearchFilter.MatchTypeEnum.Fuzzy,
      type: SearchFilter.TypeEnum.Must,
      date_to: date_to.substring(0, 10),
    };
    const activeEndDateFilter: FilterGroupFiltersInner = {
      filters: [
        {
          field: 'jobs.ended_at',
          match_type: SearchFilter.MatchTypeEnum.Exists,
          type: SearchFilter.TypeEnum.MustNot,
        },
        {
          field: 'jobs.ended_at',
          match_type: SearchFilter.MatchTypeEnum.Fuzzy,
          type: SearchFilter.TypeEnum.Must,
          date_from: searchFilter.date_from.substring(0, 10),
        },
      ],
      operator: 'or',
    };

    Log.d(
      'QuickBuildService generateJobActiveAtAPIFilters',
      activeStartDateFilter,
      activeEndDateFilter
    );

    return [activeStartDateFilter, activeEndDateFilter];
  }

  // For numeric columns, we want to return a good default interval for the field
  private static getDefaultIntervalForAgg(columnName: string): number | undefined {
    if (columnName.includes('connections')) {
      return 50;
    }

    if (columnName.includes('tenure') || columnName.includes('duration')) {
      return 6;
    }

    if (columnName.includes('employee_count')) {
      return 100;
    }

    return undefined;
  }

  private static hasValues(filter: SearchFilter): boolean {
    if (filter.match_type === SearchFilter.MatchTypeEnum.Exists) {
      return true;
    }

    if (filter.string_values && filter.string_values.length > 0) {
      return true;
    }

    if (
      (filter.boolean_value !== null && filter.boolean_value !== undefined) ||
      filter.date_from ||
      filter.date_to ||
      filter.number_max ||
      (filter.number_min !== null && filter.number_min !== undefined)
    ) {
      return true;
    }

    return false;
  }
}
